import { useEffect, useState } from 'react';
import { Alert, Box, Button, Fade, Grid, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { grey, orange } from '@mui/material/colors';
import { ConfigEquipmentForm } from '../ConfigEquipmentForm';
import { listaProdutos } from '../../../products/configs/functions';
import { useCommonItems } from '../../../../../../contexts/CommonItensProvider';
import { useAppContext } from "../../../../../../contexts/AppContext";
import ConfirmDialog from "../../../../../common/ConfirmDialog";
import { blockConfigEquipamento, unlockConfigEquipamento } from "../../configs/functions";
import { buildCells, getMotorColors, GROUP_BORDER, LEGEND_STATUS } from "./config/functions";
import { TutorialMachineThirdStepPanel } from "./components/TutorialMachineThirdStepPanel";

export function ThirdStepMachineForm({ values, equipamento, motores, setMotores, qtdMotores, restockingMode = false }) {
    const { exibirAlerta, exibirDialog, handleCloseDialog } = useCommonItems();
    const { dadosUsuario } = useAppContext();

    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));

    // No modo restocking o usuário comum também pode operar (add/remover produto).
    const canConfigure = dadosUsuario.isSempher || restockingMode;

    const cols = qtdMotores === 60 ? 10 : 6;
    const sz = isSmall ? 36 : 48;
    const gap = 8;

    const [selectedConfigMotor, setSelectedConfigMotor] = useState(null);
    const [produtos, setProdutos] = useState([]);
    const [produtosFilho, setProdutosFilho] = useState([]);
    const [statusProduto, setStatusProduto] = useState(null);
    const [resetarFormulario, setResetarFormulario] = useState(false);

    const [openBlockDialog, setOpenBlockDialog] = useState(false);
    const [openUnlockDialog, setOpenUnlockDialog] = useState(false);

    const loadProdutos = async (id_empresa, id_produto = null) => {
        exibirDialog('Carregando produtos da Empresa...');
        try {
            if (id_produto) {
                const response = await listaProdutos({ id: id_produto });
                setStatusProduto(response?.data?.data[0]?.status);
            } else {
                const response = await listaProdutos({ id_empresa, for_machine: 1 });
                setProdutos(response.data.data);
            }
        } catch (error) {
            exibirAlerta('Erro ao carregar os Produtos', '', 'error');
        } finally {
            handleCloseDialog();
        }
    };

    const blockMotor = async () => {
        try {
            const response = await blockConfigEquipamento({
                id_equipamento: selectedConfigMotor?.id_equipamento,
                numero_motor: selectedConfigMotor?.numero_motor,
            });

            if (selectedConfigMotor) {
                selectedConfigMotor.status = 'bloqueado';

                setMotores((prevMotores) =>
                    prevMotores.map((motor) =>
                        motor.numero_motor === selectedConfigMotor.numero_motor
                            ? { ...motor, status: 'bloqueado' }
                            : motor
                    )
                );
            }

            exibirAlerta(response.data.message, '', 'success');
        } catch (error) {
            const mensagemErro = error.response?.data?.error || 'Erro ao bloquear motor';
            exibirAlerta(mensagemErro, '', 'error');
        }
    };

    const unlockMotor = async () => {
        try {
            await loadProdutos(null, selectedConfigMotor.id_produto);
            const response = await unlockConfigEquipamento({
                id_equipamento: selectedConfigMotor?.id_equipamento,
                numero_motor: selectedConfigMotor?.numero_motor,
            });

            if (selectedConfigMotor) {
                const novoStatus = selectedConfigMotor.id_produto !== null ? 'ocupado' : 'livre';

                selectedConfigMotor.status = novoStatus;

                setMotores((prevMotores) =>
                    prevMotores.map((motor) =>
                        motor.numero_motor === selectedConfigMotor.numero_motor
                            ? { ...motor, status: novoStatus }
                            : motor
                    )
                );
            }

            if (!statusProduto) setResetarFormulario(true);

            exibirAlerta(response.data.message, '', 'success');
        } catch (error) {
            const mensagemErro = error.response?.data?.error || 'Erro ao desbloquear motor';
            exibirAlerta(mensagemErro, '', 'error');
        }
    };

    useEffect(() => {
        if (values && values.id_empresa) {
            loadProdutos(values.id_empresa);
        }
    }, []);

    useEffect(() => {
        const fetchProduto = async () => {
            if (selectedConfigMotor && selectedConfigMotor.id_produto) {
                const produto = produtosFilho.find(
                    (prod) => prod.id === selectedConfigMotor.id_produto
                );
                setStatusProduto(produto?.status ?? 1);
            }
        };
        fetchProduto();
    }, [selectedConfigMotor, produtosFilho]);

    const rows = motores.length > 0 ? Math.ceil(motores.length / cols) : 1;
    const cells = buildCells(motores, cols);

    const handleSelectCell = (cell) => {
        const motorToConfigure = cell.configMotor ?? cell.motor;

        // No restocking, motor bloqueado não pode ser mexido.
        if (restockingMode && motorToConfigure.status === 'bloqueado') {
            exibirAlerta(
                'Motor bloqueado',
                'Desbloqueie o motor antes de colocar ou retirar produto.',
                'warning'
            );
            return;
        }

        setSelectedConfigMotor(motorToConfigure);
    };

    const getCellSx = (cell) => {
        const { gridRow, gridCol, colSpan, grouped } = cell;

        const visualMotor = cell.configMotor ?? cell.motor;
        const isSelected = selectedConfigMotor && Number(selectedConfigMotor.numero_motor) === Number(visualMotor.numero_motor);

        const w = colSpan * sz + (colSpan - 1) * gap;

        const base = {
            gridColumn: `${gridCol} / span ${colSpan}`,
            gridRow: `${gridRow} / span 1`,
            width: `${w}px`, height: `${sz}px`,
            minWidth: 0, p: 0,
            fontSize: 12, fontWeight: 500, borderRadius: 1.5,
            transform: isSelected ? 'scale(1.04)' : 'none',
            transition: 'all .15s',
        };

        const c = getMotorColors(visualMotor.status);

        // Borda: agrupado -> tracejada destacada; selecionado -> laranja; senão -> status.
        let border;
        if (isSelected) {
            border = `2.5px solid ${orange[600]}`;
        } else if (grouped) {
            border = GROUP_BORDER;
        } else {
            border = `1.5px solid ${c.border}`;
        }

        return {
            ...base,
            backgroundColor: isSelected ? orange[100] : c.bg,
            border,
            color: isSelected ? orange[900] : c.color,
            '&:hover': { backgroundColor: isSelected ? orange[200] : c.hoverBg, color: 'white' },
            '&.Mui-disabled': { backgroundColor: c.bg, color: c.color, opacity: 1 },
        };
    };

    const getTooltipTitle = (cell) => {
        const motor = cell.configMotor ?? cell.motor;
        if (motor.status === 'bloqueado') {
            return restockingMode
                ? 'Motor bloqueado — desbloqueie antes de colocar/retirar produto.'
                : 'Motor bloqueado — desbloqueie antes de configurar.';
        }
        if (cell.grouped) {
            return 'Clique para configurar o motor agrupado';
        }
        return restockingMode
            ? (motor.id_produto ? 'Clique para remover o produto' : 'Clique para adicionar um produto')
            : 'Clique para configurar o motor';
    };

    return (
        <Grid container direction="row" justifyContent="center" alignItems="flex-start" spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
                <Typography variant="h5" textAlign="center" gutterBottom>
                    {restockingMode ? 'Reabastecimento' : 'Configurações do Equipamento'}
                </Typography>
            </Grid>

            <Grid item xs={12} md={7}>
                <Box sx={{ border: `1px solid ${grey[300]}`, borderRadius: 2, p: 2 }}>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${cols}, ${sz}px)`,
                        gridTemplateRows:    `repeat(${rows}, ${sz}px)`,
                        gap: `${gap}px`,
                        justifyContent: 'center',
                    }}>
                        {cells.map((cell) => {
                            const { motor, partner, physIdx, grouped } = cell;

                            return (
                                <Tooltip key={physIdx} title={getTooltipTitle(cell)} placement="top">
                                    <span style={{
                                        gridColumn: `${cell.gridCol} / span ${cell.colSpan}`,
                                        gridRow: `${cell.gridRow} / span 1`,
                                    }}>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            fullWidth
                                            onClick={() => handleSelectCell(cell)}
                                            sx={getCellSx(cell)}
                                        >
                                            {grouped
                                                ? `${motor.numero_motor} + ${partner.numero_motor}`
                                                : motor.numero_motor}
                                        </Button>
                                    </span>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mt: 1.5, pl: .5 }}>
                    {LEGEND_STATUS.map(l => (
                        <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                            <Box sx={{
                                width: 14, height: 14, borderRadius: '3px',
                                backgroundColor: l.bg,
                                border: l.dashed ? `2px dashed ${l.border}` : `1.5px solid ${l.border}`,
                            }} />
                            <Typography variant="caption" color="text.secondary">{l.label}</Typography>
                        </Box>
                    ))}
                </Box>

                {restockingMode && motores.some(m => m.status === 'bloqueado') && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                        Motores bloqueados não podem ser reabastecidos por aqui.
                    </Alert>
                )}

                {/* Botões de bloquear/desbloquear: só fora do restocking. */}
                {selectedConfigMotor && dadosUsuario.isSempher && !restockingMode && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                type="button"
                                onClick={() => setOpenBlockDialog(true)}
                                disabled={selectedConfigMotor.status === 'bloqueado'}
                            >
                                Bloquear motor
                            </Button>
                            <Tooltip
                                title={
                                    selectedConfigMotor?.id_produto && statusProduto === 0
                                        ? 'Você não pode desbloquear este motor porque o produto está inativo'
                                        : ''
                                }
                            >
                                <span>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        disabled={
                                            selectedConfigMotor?.status !== 'bloqueado'
                                            || (!dadosUsuario.isSempher && (selectedConfigMotor.id_produto && statusProduto !== 1))
                                        }
                                        onClick={() => setOpenUnlockDialog(true)}
                                    >
                                        Desbloquear motor
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Box>
                )}
            </Grid>

            <Grid item xs={12} md={4}>
                {!restockingMode && (
                    <Fade in={selectedConfigMotor === null} unmountOnExit>
                        <Box><TutorialMachineThirdStepPanel /></Box>
                    </Fade>
                )}

                {restockingMode && (
                    <Fade in={selectedConfigMotor === null} unmountOnExit>
                        <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
                            <Typography variant="body1">
                                Selecione um motor para adicionar ou remover um produto.
                            </Typography>
                        </Box>
                    </Fade>
                )}

                <Fade in={selectedConfigMotor !== null} unmountOnExit>
                    <Box>
                        <ConfigEquipmentForm
                            equipamento={equipamento}
                            produtos={produtos}
                            configEquipamento={selectedConfigMotor}
                            motores={motores}
                            setMotores={setMotores}
                            produtosFilho={produtosFilho}
                            setProdutosFilho={setProdutosFilho}
                            resetarFormulario={resetarFormulario}
                            setResetarFormulario={setResetarFormulario}
                            readOnly={!canConfigure}
                            restockingMode={restockingMode}
                        />

                        {restockingMode && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                No reabastecimento, o estoque do produto é sempre definido como <strong>0</strong> e
                                não pode ser alterado aqui. Você pode adicionar ou remover o produto do motor;
                                o ajuste de quantidade é feito posteriormente através do processo de reabastecimento
                                da máquina.
                            </Alert>
                        )}
                    </Box>
                </Fade>
            </Grid>

            <ConfirmDialog
                title="Confirmar bloqueio"
                description={
                    <>
                        O bloqueio deste motor tornará sua utilização indisponível na máquina.
                        Confirma que deseja prosseguir com o bloqueio?
                    </>
                }
                state={openBlockDialog}
                handleClose={() => setOpenBlockDialog(false)}
                goAction={async () => {
                    await blockMotor();
                    setOpenBlockDialog(false);
                }}
            />

            <ConfirmDialog
                title="Confirmar desbloqueio"
                description={statusProduto ? "Desbloquear motor?" : "Este motor está vinculado a um produto inativo. Ao desbloqueá-lo, os dados atuais serão apagados para dar lugar a um produto ativo. Deseja continuar?"}
                state={openUnlockDialog}
                handleClose={() => setOpenUnlockDialog(false)}
                goAction={async () => {
                    await unlockMotor();
                    setOpenUnlockDialog(false);
                    if (statusProduto === 0) setResetarFormulario(true);
                }}
            />
        </Grid>
    );
}